import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Typography } from '@mui/material';
import { FormDialog } from './FormDialog';
import { InlineNotice } from './InlineNotice';

export type ConfirmationIntent = 'confirm' | 'delete' | 'discard' | 'archive';

export type ConfirmationOptions = {
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  intent?: ConfirmationIntent;
};

export type ConfirmDialogProps = ConfirmationOptions & {
  open: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  busy?: boolean;
  busyLabel?: string;
  confirmDisabled?: boolean;
  error?: ReactNode;
  errorAction?: ReactNode;
  children?: ReactNode;
};

/** 대상과 영향은 호출자가 설명하고, 취소 우선 초점과 비동기 처리 중 닫기·중복 실행은 공용으로 관리합니다. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = '취소',
  intent = 'confirm',
  onConfirm,
  onCancel,
  busy = false,
  busyLabel = '처리 중',
  confirmDisabled = false,
  error,
  errorAction,
  children,
}: ConfirmDialogProps) {
  const descriptionId = useId();
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const activeError = error || actionError;

  useEffect(() => {
    if (!open) setActionError(undefined);
  }, [open]);

  const confirm = async () => {
    if (busy || pendingRef.current || confirmDisabled) return;
    pendingRef.current = true;
    setPending(true);
    setActionError(undefined);
    try {
      await onConfirm();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : '처리하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return <FormDialog
    open={open}
    title={title}
    onClose={onCancel}
    onSubmit={confirm}
    cancelLabel={cancelLabel}
    submitLabel={confirmLabel}
    submitColor={intent === 'delete' || intent === 'discard' ? 'error' : intent === 'archive' ? 'warning' : 'primary'}
    busy={busy || pending}
    busyLabel={busyLabel}
    submitDisabled={confirmDisabled}
    initialFocus="cancel"
    aria-describedby={descriptionId}
    maxWidth="xs"
    mobilePresentation="dialog"
  >
    <Typography id={descriptionId} variant="body2">{description}</Typography>
    {children}
    {activeError ? <InlineNotice severity="error" announcement="polite" action={errorAction}>{activeError}</InlineNotice> : null}
  </FormDialog>;
}

/** boolean 또는 Promise<boolean> 확인 callback을 받는 기존 흐름에서 사용할 수 있습니다. */
export function useConfirmation() {
  const [options, setOptions] = useState<ConfirmationOptions>();
  const pending = useRef<((confirmed: boolean) => void) | undefined>(undefined);
  const settle = useCallback((confirmed: boolean) => {
    const resolve = pending.current;
    pending.current = undefined;
    setOptions(undefined);
    resolve?.(confirmed);
  }, []);
  const confirm = useCallback((next: ConfirmationOptions): Promise<boolean> => {
    // 진행 중인 확인을 다른 행동이 덮어쓰거나 첫 callback을 남겨두지 않습니다.
    if (pending.current) return Promise.resolve(false);
    return new Promise((resolve) => {
      pending.current = resolve;
      setOptions(next);
    });
  }, []);

  useEffect(() => () => {
    pending.current?.(false);
    pending.current = undefined;
  }, []);

  return {
    confirm,
    confirmationDialog: options ? <ConfirmDialog
      {...options}
      open
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    /> : null,
  };
}
